import * as React from "react";
import { CheckIcon, ChevronDownIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/library/popover";
import { bulkSelection, matchesSearch, type BulkItem } from "./multi-select.bulk";

/**
 * Pins the Radix popper wrapper (the floating element parent of our
 * PopoverContent) to the trigger's width. Radix sets `min-width:
 * max-content` inline on the wrapper so it can be measured before
 * positioning — that breaks our constraint because the wrapper then
 * grows to fit a long item label and the whole popover shifts leftward.
 *
 * Pure-CSS overrides via `:has()` were observed not to take effect in
 * some build/HMR combinations, so we drive it imperatively: read
 * `--radix-popper-anchor-width` (which Radix writes on the same
 * wrapper) and copy it onto `width` / `maxWidth`, plus zero out
 * `minWidth`. A `MutationObserver` on the wrapper's `style` keeps us
 * in sync with Radix's own re-layouts (open, reposition, resize).
 */
function useClampPopperWrapper(contentRef: React.RefObject<HTMLElement | null>) {
  React.useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    // Walk up to the popper wrapper. Usually it's the direct parent, but
    // we guard in case Radix ever introduces an intermediate element.
    let wrapper: HTMLElement | null = content.parentElement;
    while (
      wrapper &&
      !wrapper.hasAttribute("data-radix-popper-content-wrapper")
    ) {
      wrapper = wrapper.parentElement;
    }
    if (!wrapper) return;

    const apply = () => {
      // Read whichever anchor-width var Radix has populated.
      const anchorWidth =
        wrapper!.style.getPropertyValue("--radix-popper-anchor-width").trim() ||
        wrapper!.style.getPropertyValue("--radix-popover-trigger-width").trim();
      if (!anchorWidth) return;
      // `setProperty` with `'important'` is the only way to outrank
      // React's inline-style reconciliation (Radix re-applies
      // `min-width: max-content` on every render of the wrapper).
      wrapper!.style.setProperty("min-width", "0px", "important");
      wrapper!.style.setProperty("width", anchorWidth, "important");
      wrapper!.style.setProperty("max-width", anchorWidth, "important");
    };

    apply();
    const observer = new MutationObserver(apply);
    observer.observe(wrapper, {
      attributes: true,
      attributeFilter: ["style"],
    });
    return () => observer.disconnect();
  }, [contentRef]);
}

type MultiSelectContextValue = {
  value: string[];
  onValueChange: (value: string[]) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  search: string;
  onSearchChange: (value: string) => void;
  /** Items announce themselves so the header can offer "Select all" over
   *  whatever is on the list — see ./multi-select.bulk. Stable callbacks. */
  registerItem: (item: BulkItem) => void;
  unregisterItem: (value: string) => void;
};

const MultiSelectContext = React.createContext<
  MultiSelectContextValue | undefined
>(undefined);

/** The registry lives in its OWN context so that an item mounting or
 *  relabelling re-renders the "Select all" row and nothing else. */
type MultiSelectItemsValue = { items: Map<string, BulkItem>; version: number };
const MultiSelectItemsContext = React.createContext<MultiSelectItemsValue>({
  items: new Map(),
  version: 0,
});

/** The leads page's CF_NONE_SENTINEL ("no value") — a contradiction next to
 *  real values, so never part of a bulk pick. Matched here by value so the
 *  27 pickers need no call-site change. */
const SENTINEL_VALUE = "none";

function useMultiSelectContext() {
  const context = React.useContext(MultiSelectContext);
  if (!context) {
    throw new Error(
      "MultiSelect components must be used within a MultiSelect component"
    );
  }
  return context;
}

function MultiSelect({
  value,
  onValueChange,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof Popover> & {
  value: string[];
  onValueChange: (value: string[]) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  // The registry is ONE Map for the life of the component (state, not a ref,
  // so no ref is read during render); `itemsVersion` is its change counter
  // and the only thing the "Select all" row re-renders on.
  const [itemsMap] = React.useState(() => new Map<string, BulkItem>());
  const [itemsVersion, setItemsVersion] = React.useState(0);
  const registerItem = React.useCallback((item: BulkItem) => {
    const prev = itemsMap.get(item.value);
    if (prev && prev.label === item.label && prev.disabled === item.disabled && prev.bulk === item.bulk) return;
    itemsMap.set(item.value, item);
    setItemsVersion((v) => v + 1);
  }, [itemsMap]);
  const unregisterItem = React.useCallback((value: string) => {
    if (itemsMap.delete(value)) setItemsVersion((v) => v + 1);
  }, [itemsMap]);
  const itemsValue = React.useMemo<MultiSelectItemsValue>(
    () => ({ items: itemsMap, version: itemsVersion }),
    [itemsMap, itemsVersion],
  );

  // `open` is internal state, so an `onOpenChange` passed by a caller must be
  // COMPOSED, not forwarded to the Popover — the props spread below lands after
  // `open`/`onOpenChange`, so forwarding would replace the state setter and the
  // dropdown would never open. Callers use this to react to the popover
  // CLOSING (i.e. "the user finished choosing") rather than to every checkbox
  // toggle.
  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (!nextOpen) setSearch("");
      onOpenChange?.(nextOpen);
    },
    [onOpenChange],
  );

  return (
    <MultiSelectContext.Provider
      value={{
        value,
        onValueChange,
        open,
        onOpenChange: handleOpenChange,
        search,
        onSearchChange: setSearch,
        registerItem,
        unregisterItem,
      }}
    >
      <MultiSelectItemsContext.Provider value={itemsValue}>
        <Popover open={open} onOpenChange={handleOpenChange} {...props} />
      </MultiSelectItemsContext.Provider>
    </MultiSelectContext.Provider>
  );
}

/**
 * Clamps the trigger button's rendered width to its parent's content-box
 * width, regardless of intrinsic flex sizing of its long
 * `whitespace-nowrap` content. CSS-only constraints (`w-full min-w-0
 * overflow-hidden truncate`) were observed to lose against the
 * Radix-Slot prop merge in some builds, so we measure the parent and
 * write the width imperatively on mount and on every parent resize.
 */
function useClampTriggerWidth(ref: React.RefObject<HTMLButtonElement | null>) {
  React.useLayoutEffect(() => {
    const el = ref.current;
    const parent = el?.parentElement;
    if (!el || !parent) return;

    const apply = () => {
      // clientWidth is the content-box width of the parent (px).
      const w = parent.clientWidth;
      if (!w) return;
      const px = `${w}px`;
      if (el.style.maxWidth !== px) el.style.maxWidth = px;
      if (el.style.width !== px) el.style.width = px;
      if (el.style.minWidth !== "0px") el.style.minWidth = "0px";
    };

    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(parent);
    return () => observer.disconnect();
  }, [ref]);
}

function MultiSelectTrigger({
  className,
  size = "default",
  children,
  ...props
}: React.ComponentProps<typeof PopoverTrigger> & {
  size?: "sm" | "default";
}) {
  const { open } = useMultiSelectContext();
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  useClampTriggerWidth(buttonRef);

  return (
    <PopoverTrigger
      data-slot="multi-select-trigger"
      data-size={size}
      asChild
      {...props}
    >
      <button
        ref={buttonRef}
        type="button"
        data-slot="multi-select-trigger"
        className={cn(
          "xale-multi-select-trigger",
          "border-input data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 flex w-full min-w-0 items-center justify-between gap-2 overflow-hidden rounded-md border bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-9 data-[size=sm]:h-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 cursor-pointer",
          className
        )}
        style={{
          borderRadius: ".8rem",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        {children}
        <ChevronDownIcon
          className={cn(
            "size-4 shrink-0 opacity-50 transition-transform",
            open && "rotate-180"
          )}
        />
      </button>
    </PopoverTrigger>
  );
}

function MultiSelectValue({
  className,
  placeholder,
  options,
  style,
  ...props
}: React.HTMLAttributes<HTMLSpanElement> & {
  placeholder?: string;
  options?: Array<{ value: string; label: React.ReactNode }>;
}) {
  const { value } = useMultiSelectContext();

  // Pick selected labels in the order the OPTIONS define (not the order
  // the user clicked), so the trigger reads predictably and the "first"
  // label is stable across re-opens. Falls back to raw values when no
  // options map is provided so the component still works in headless use.
  const selectedLabels: React.ReactNode[] = React.useMemo(() => {
    if (value.length === 0) return [];
    if (options) {
      return options
        .filter((opt) => value.includes(opt.value))
        .map((opt) => opt.label ?? opt.value);
    }
    return value;
  }, [value, options]);

  return (
    <span
      data-slot="multi-select-value"
      className={cn(
        "xale-multi-select-value",
        // Flex row so the first label can truncate independently of the
        // "+N" count chip, which always stays fully visible. Min-w-0 on
        // the wrapper + min-w-0 on the label cell is what unlocks the
        // child truncate (flex defaults to min-content otherwise).
        "flex-1 min-w-0 flex items-center gap-1.5 text-left",
        value.length === 0 && "text-muted-foreground",
        className
      )}
      {...props}
      style={{
        flex: "1 1 0%",
        minWidth: 0,
        ...(style ?? {}),
      }}
    >
      {selectedLabels.length === 0 ? (
        <span className="truncate">{placeholder || "Select options"}</span>
      ) : (
        <>
          <span
            className="truncate min-w-0"
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selectedLabels[0]}
          </span>
          {selectedLabels.length > 1 && (
            <span
              className="shrink-0 rounded-full bg-[var(--color-black-2)] text-[var(--color-text-secondary)] px-1.5 py-0.5 text-[11px] leading-none font-medium"
              aria-label={`${selectedLabels.length - 1} more selected`}
              title={selectedLabels.slice(1).map((l) => String(l)).join(", ")}
            >
              +{selectedLabels.length - 1}
            </span>
          )}
        </>
      )}
    </span>
  );
}

/**
 * "Select all" / "Clear all" over the rows the user can currently see (all of
 * them, or the search matches), under the search box. Renders nothing while
 * fewer than two rows are on offer — one row is a click, not a bulk action.
 */
function MultiSelectBulkRow() {
  const { value, onValueChange, search } = useMultiSelectContext();
  const { items, version } = React.useContext(MultiSelectItemsContext);
  const selection = React.useMemo(
    () => bulkSelection(items.values(), value, search),
    // `version` is the registry's change counter — the Map itself is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, version, value, search],
  );
  if (selection.visible.length < 2) return null;
  const searching = search.trim() !== "";
  const total = [...items.values()].filter((i) => i.bulk && !i.disabled).length;
  return (
    <div
      data-slot="multi-select-bulk"
      className="flex shrink-0 items-center justify-between gap-2 border-b border-[var(--color-black-2)] bg-popover px-3 py-1.5 text-[12px]"
    >
      <span className="truncate text-[var(--color-black-6)]">
        {searching ? `${selection.visible.length} of ${total} match` : `${total} options`}
      </span>
      <button
        type="button"
        aria-pressed={selection.allSelected}
        onClick={() => onValueChange(selection.next)}
        className="shrink-0 cursor-pointer rounded-md px-1.5 py-0.5 font-medium text-[var(--color-primary)] hover:bg-[var(--color-green-1)] focus-visible:outline-none focus-visible:ring-[2px] focus-visible:ring-ring/50"
      >
        {selection.allSelected ? "Clear all" : "Select all"}
      </button>
    </div>
  );
}

function MultiSelectContent({
  className,
  align = "start",
  sideOffset = 6,
  style,
  children,
  searchPlaceholder = "Search options...",
  selectAll = true,
  ...props
}: React.ComponentProps<typeof PopoverContent> & {
  searchPlaceholder?: string;
  /** The "Select all" / "Clear all" row under the search — on by default. */
  selectAll?: boolean;
}) {
  const { search, onSearchChange } = useMultiSelectContext();
  const contentRef = React.useRef<HTMLDivElement>(null);
  useClampPopperWrapper(contentRef);

  return (
    <PopoverContent
      ref={contentRef}
      data-slot="multi-select-content"
      className={cn(
        // PopoverContent is the flex column shell — the SEARCH header is
        // a non-shrinking sibling, and the OPTIONS list below it owns
        // its own scroll. Doing it this way (instead of overflow on the
        // popover with a sticky header) sidesteps three bugs we hit:
        //   1. Some parent sheets create overflow contexts that
        //      shadow-clipped the popover's own overflow scroll, so
        //      the wheel/trackpad event never reached the list.
        //   2. Radix's open-state `zoom-in-95` animation briefly
        //      transformed the scroll container, leaving the sticky
        //      header detached during the animation frame.
        //   3. Tailwind v4's arbitrary-value parser silently dropped
        //      `max-h-[min(60vh,22rem)]` in some build combinations.
        // The inline width/maxWidth/minWidth still need to override
        // Radix's wrapper `min-width: max-content`; see comments below.
        "p-0 overscroll-contain box-border flex flex-col",
        className
      )}
      style={{
        // `contain: inline-size` is the key bit: it makes the popover
        // content opaque to its parent's intrinsic-size calculations, so
        // Radix's wrapper `min-width: max-content` resolves against our
        // explicit width rather than the children's max-content (which
        // would be the longest item label and push the wrapper wider
        // than the trigger).
        contain: "inline-size",
        width: "var(--radix-popover-trigger-width)",
        maxWidth: "var(--radix-popover-trigger-width)",
        minWidth: 0,
        borderRadius: ".8rem",
        // Cap total popover height (header + scroll area). Inline
        // because Tailwind v4 drops arbitrary values containing commas
        // in min()/max() in some builds.
        maxHeight: "min(70vh, 28rem)",
        ...style,
      }}
      align={align}
      sideOffset={sideOffset}
      {...props}
    >
      <div className="shrink-0 border-b border-[var(--color-black-2)] bg-popover p-2">
        <input
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={searchPlaceholder}
          className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[2px]"
        />
      </div>
      {selectAll && <MultiSelectBulkRow />}
      <div
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-1"
        // Wheel-event leak prevention: stop the popover's scroll from
        // bubbling up into a parent sheet/scroll-area when the user
        // bottoms-out the list.
        onWheelCapture={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </PopoverContent>
  );
}

/**
 * Recursively pull plain text out of a React children tree so the
 * MultiSelect search can match against it. The previous version only
 * handled raw `string | number` children and shallow arrays — any caller
 * that wrapped its row in a `<span>` (e.g. `CampaignFilter`'s label + Meta
 * badge) landed in the fallback branch and got `itemValue` (the numeric
 * id) as the searchable label, which made search appear "not working".
 */
function extractSearchText(node: React.ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(extractSearchText).join(" ");
  if (React.isValidElement(node)) {
    const props = node.props as { children?: React.ReactNode } | undefined;
    return extractSearchText(props?.children);
  }
  return "";
}

function MultiSelectItem({
  className,
  value: itemValue,
  children,
  disabled,
  searchLabel,
  bulk,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  value: string;
  /** Optional override for the searchable label (useful when the visible
   * children contain extra non-searchable markup like icons). Defaults
   * to the concatenated text content of `children`. */
  searchLabel?: string;
  /** Part of "Select all"? Defaults to true, except for the "none" sentinel. */
  bulk?: boolean;
}) {
  const { value, onValueChange, search, registerItem, unregisterItem } = useMultiSelectContext();
  const selected = value.includes(itemValue);
  const label = React.useMemo(() => {
    if (searchLabel != null && searchLabel !== "") return searchLabel;
    const extracted = extractSearchText(children).trim();
    return extracted.length > 0 ? extracted : itemValue;
  }, [children, itemValue, searchLabel]);
  const inBulk = bulk ?? itemValue !== SENTINEL_VALUE;

  // Announce this row to the header's "Select all" — a row hidden by the
  // search still registers (it stays mounted, it just renders nothing), so
  // the registry is the whole list and the search is applied once, up there.
  React.useEffect(() => {
    registerItem({ value: itemValue, label, disabled: !!disabled, bulk: inBulk });
    return () => unregisterItem(itemValue);
  }, [itemValue, label, disabled, inBulk, registerItem, unregisterItem]);

  if (!matchesSearch(label, search)) {
    return null;
  }

  const toggleOption = () => {
    const next = selected
      ? value.filter((v) => v !== itemValue)
      : [...value, itemValue];
    onValueChange(next);
  };

  return (
    <button
      type="button"
      data-slot="multi-select-item"
      disabled={disabled}
      onClick={toggleOption}
      className={cn(
        "focus:bg-accent focus:text-accent-foreground relative flex w-full min-w-0 cursor-pointer items-center gap-2 overflow-hidden rounded-sm py-1.5 pr-8 pl-2 text-sm select-none transition-colors text-left",
        selected && "bg-accent text-accent-foreground",
        disabled && "pointer-events-none opacity-50",
        className
      )}
      style={{ borderRadius: ".8rem" }}
      {...props}
    >
      <span className="absolute right-2 flex size-3.5 items-center justify-center">
        {selected && <CheckIcon className="size-4" />}
      </span>
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </button>
  );
}

export {
  MultiSelect,
  MultiSelectContent,
  MultiSelectItem,
  MultiSelectTrigger,
  MultiSelectValue,
  useMultiSelectContext,
};
